import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTooltip } from '../components/TooltipSystem';
import {
  Trophy, Plus, ArrowLeft, Trash2, Edit2, Award, Users, Star,
  Sparkles, Crown, Gem, Shield, Gamepad2, Flame, Target, Eye
} from 'lucide-react';

const RARITIES = [
  { id: 'comune', label: 'Comune', color: '#64748B', bg: 'bg-slate-100', icon: Shield },
  { id: 'raro', label: 'Raro', color: '#3B82F6', bg: 'bg-blue-50', icon: Star },
  { id: 'epico', label: 'Epico', color: '#8B5CF6', bg: 'bg-violet-50', icon: Gem },
  { id: 'leggendario', label: 'Leggendario', color: '#F59E0B', bg: 'bg-amber-50', icon: Crown },
];

const UNLOCK_TYPES = [
  { id: 'game_wins', label: 'Vittorie gioco', desc: 'Vinci N partite a un gioco' },
  { id: 'streak_days', label: 'Giorni streak', desc: 'Raggiungi N giorni consecutivi' },
  { id: 'special_match_top', label: 'Top classifica', desc: 'Finisci tra i primi N in una partita speciale' },
  { id: 'total_games', label: 'Partite totali', desc: 'Gioca N partite in totale' },
  { id: 'total_points', label: 'Punti totali', desc: 'Accumula N punti' },
  { id: 'manual', label: 'Manuale', desc: 'Assegnato dall\'admin' },
];

function RarityBadge({ rarity }) {
  const r = RARITIES.find(x => x.id === rarity) || RARITIES[0];
  const Icon = r.icon;
  return (
    <span className={`${r.bg} inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold border`}
      style={{ color: r.color, borderColor: `${r.color}30` }}>
      <Icon size={10} /> {r.label}
    </span>
  );
}

/* ── Trophy Form ── */
function TrophyForm({ trophy, onSave, onCancel }) {
  const [form, setForm] = useState(trophy || {
    name: '', description: '', image_url: '', rarity: 'comune', set_name: '',
    unlock_type: 'manual', unlock_value: 0, unlock_game_type: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Nome obbligatorio'); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#1A1A1A] transition-colors">
        <ArrowLeft size={16} /> Annulla
      </button>
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm space-y-5">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">{trophy ? 'Modifica trofeo' : 'Nuovo trofeo'}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Nome</label>
            <input value={form.name} onChange={e => upd('name', e.target.value)} data-testid="trophy-name-input"
              placeholder="es: Primo Passo" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Set (opzionale)</label>
            <input value={form.set_name} onChange={e => upd('set_name', e.target.value)} data-testid="trophy-set-input"
              placeholder="es: Wordle Master" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Descrizione</label>
          <textarea value={form.description} onChange={e => upd('description', e.target.value)} rows={2} data-testid="trophy-desc-input"
            placeholder="Come si sblocca questo trofeo?" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none resize-none" />
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Rarità</label>
          <div className="flex gap-2">
            {RARITIES.map(r => {
              const Icon = r.icon;
              return (
                <button key={r.id} onClick={() => upd('rarity', r.id)} data-testid={`rarity-${r.id}`}
                  className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${form.rarity === r.id ? `${r.bg}` : 'hover:border-slate-300'}`}
                  style={{ borderColor: form.rarity === r.id ? r.color : '#e2e8f0', color: form.rarity === r.id ? r.color : '#64748B' }}>
                  <Icon size={18} className="mx-auto mb-1" />
                  <p className="text-xs font-bold">{r.label}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Condizione sblocco</label>
            <select value={form.unlock_type} onChange={e => upd('unlock_type', e.target.value)} data-testid="unlock-type-select"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
              {UNLOCK_TYPES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
          {form.unlock_type !== 'manual' && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Valore soglia</label>
              <input type="number" value={form.unlock_value} onChange={e => upd('unlock_value', parseInt(e.target.value) || 0)} data-testid="unlock-value-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
          )}
          {form.unlock_type === 'game_wins' && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Tipo gioco</label>
              <select value={form.unlock_game_type} onChange={e => upd('unlock_game_type', e.target.value)}
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
                <option value="">Qualsiasi</option>
                <option value="wordle">Wordle</option>
                <option value="trivia">Trivia</option>
                <option value="tango">Tango</option>
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">URL immagine (opzionale)</label>
          <input value={form.image_url} onChange={e => upd('image_url', e.target.value)} data-testid="trophy-image-input"
            placeholder="https://..." className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onCancel} className="px-5 py-2.5 rounded-xl text-sm font-medium text-[#64748B] border border-slate-200 hover:bg-slate-50 transition-colors">Annulla</button>
          <button onClick={handleSubmit} disabled={saving} data-testid="save-trophy-btn"
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] disabled:opacity-50 transition-colors">
            {saving ? 'Salvataggio...' : trophy ? 'Salva' : 'Crea trofeo'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function AdminTrophies() {
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.get('/admin/trophies');
      setTrophies(r.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (data) => {
    await adminApi.post('/admin/trophies', data);
    toast.success('Trofeo creato!');
    setView('list'); load();
  };

  const handleUpdate = async (data) => {
    await adminApi.put(`/admin/trophies/${selected.id}`, data);
    toast.success('Trofeo aggiornato!');
    setView('list'); load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo trofeo?')) return;
    try { await adminApi.delete(`/admin/trophies/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // Group by rarity
  const grouped = RARITIES.map(r => ({
    ...r,
    items: trophies.filter(t => t.rarity === r.id),
  })).filter(g => g.items.length > 0);

  const totalUnlocks = trophies.reduce((s, t) => s + (t.unlock_count || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;
  if (view === 'create') return <div className="p-6 max-w-3xl mx-auto"><TrophyForm onSave={handleCreate} onCancel={() => setView('list')} /></div>;
  if (view === 'edit' && selected) return <div className="p-6 max-w-3xl mx-auto"><TrophyForm trophy={selected} onSave={handleUpdate} onCancel={() => setView('list')} /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Trophy size={22} className="text-[#F59E0B]" /> Trofei</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestisci i trofei collezionabili sbloccati dai clienti</p>
        </div>
        <button onClick={() => setView('create')} data-testid="create-trophy-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors active:scale-95">
          <Plus size={16} /> Nuovo trofeo
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {RARITIES.map(r => {
          const count = trophies.filter(t => t.rarity === r.id).length;
          const Icon = r.icon;
          return (
            <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <PageTooltip page="trophies" />
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${r.color}15` }}>
                  <Icon size={16} style={{ color: r.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold text-[#1A1A1A]">{count}</p>
                  <p className="text-[10px] text-[#64748B] font-medium uppercase tracking-wider">{r.label}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Trophy list grouped by rarity */}
      {trophies.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
          <Trophy size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-[#1A1A1A] font-semibold">Nessun trofeo creato</p>
          <p className="text-sm text-[#64748B] mt-1">Crea il tuo primo trofeo collezionabile</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(group => (
            <div key={group.id}>
              <div className="flex items-center gap-2 mb-3">
                <group.icon size={14} style={{ color: group.color }} />
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: group.color }}>{group.label} ({group.items.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <AnimatePresence>
                  {group.items.map((t, idx) => (
                    <motion.div key={t.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.03 }}
                      data-testid={`trophy-card-${t.id}`}
                      className="bg-white rounded-2xl border border-slate-200/60 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {t.image_url ? (
                            <img src={t.image_url} alt="" className="w-10 h-10 rounded-xl object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${group.color}15` }}>
                              <Trophy size={18} style={{ color: group.color }} />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold text-[#1A1A1A]">{t.name}</p>
                            {t.set_name && <p className="text-[10px] text-[#64748B]">Set: {t.set_name}</p>}
                          </div>
                        </div>
                        <RarityBadge rarity={t.rarity} />
                      </div>
                      {t.description && <p className="text-xs text-[#64748B] mb-2 line-clamp-2">{t.description}</p>}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                          <Users size={10} /> {t.unlock_count || 0} sbloccati
                        </span>
                        <div className="flex gap-1">
                          <button onClick={() => { setSelected(t); setView('edit'); }} data-testid={`edit-trophy-${t.id}`}
                            className="p-1.5 rounded-lg text-[#64748B] hover:bg-slate-100 transition-colors"><Edit2 size={12} /></button>
                          <button onClick={() => handleDelete(t.id)} data-testid={`delete-trophy-${t.id}`}
                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
